// Downloads from the browser: one file as it is stored, or several zipped.
//
// Everything is fetched and packed in the tab — the audio is already
// reachable (the object proxy a room plays from) and a seed is tens of
// megabytes, not gigabytes. The zip stores, it does not deflate: audio does
// not compress, and storing is a copy.
//
// Signed in only (the callers gate on it): the object proxy opens a seed's
// audio for an account, and a download is counted against one.

import { zipSync } from "fflate";
import type { AnalyticsEventName } from "@shared/analytics-events.js";
import { audioFetch, objectUrl } from "@web-shared/lib/api";
import { track } from "@web-shared/lib/analytics";
import { json, webApi } from "@web-shared/lib/web-api";

/** One file of a download: where it goes in the zip, and where its bytes
 * are — a URL to fetch, or bytes already in hand (a render). */
export interface DownloadFile {
  path: string;
  url?: string;
  bytes?: Uint8Array;
}

/** A name made safe for a file or a folder. */
export function safeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/\s+/g, " ").replace(/^[.\s]+|[.\s]+$/g, "") || "untitled";
}

/** The extension an object's key ends in (".wav" when it has none). */
export function extOfKey(key: string): string {
  const m = /\.([a-z0-9]{2,5})$/i.exec(key);
  return m ? `.${m[1]!.toLowerCase()}` : ".wav";
}

/** `paths`, with repeats told apart: "Kick.wav", "Kick 2.wav". */
export function uniquePaths<T extends { path: string }>(files: T[]): T[] {
  const seen = new Map<string, number>();
  return files.map((f) => {
    const key = f.path.toLowerCase();
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    if (n === 1) return f;
    const dot = f.path.lastIndexOf(".");
    const slash = f.path.lastIndexOf("/");
    const path = dot > slash ? `${f.path.slice(0, dot)} ${n}${f.path.slice(dot)}` : `${f.path} ${n}`;
    return { ...f, path };
  });
}

/** Where a stored object is fetched from, by key. */
export function keyUrl(key: string): string {
  return objectUrl(key);
}

/** Hand the browser a file to save. */
export function saveBytes(filename: string, bytes: Uint8Array | ArrayBuffer, type = "application/octet-stream"): void {
  const blob = new Blob([bytes as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function fetchBytes(url: string): Promise<Uint8Array> {
  const res = await audioFetch(url);
  if (!res.ok) throw new Error(`Couldn't fetch the audio (${res.status}).`);
  return new Uint8Array(await res.arrayBuffer());
}

function mimeOf(name: string): string {
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  return ext === "wav" ? "audio/wav" : ext === "mp3" ? "audio/mpeg" : ext === "flac" ? "audio/flac" : ext === "aif" || ext === "aiff" ? "audio/aiff" : "application/octet-stream";
}

const CONCURRENCY = 3;

/**
 * Save `files`: one as itself (named by the last part of its path), several
 * as `<name>.zip`. Files that cannot be fetched are left out rather than
 * failing the rest; with none left, it throws. Returns how many were saved.
 */
export async function download(
  name: string,
  files: DownloadFile[],
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  const wanted = uniquePaths(files);
  if (wanted.length === 0) throw new Error("There is no audio to download.");
  const got: Array<{ path: string; bytes: Uint8Array }> = [];
  let done = 0;
  let next = 0;
  let lastError: unknown = null;
  onProgress?.(0, wanted.length);
  const worker = async (): Promise<void> => {
    while (next < wanted.length) {
      const file = wanted[next++]!;
      try {
        const bytes = file.bytes ?? (file.url ? await fetchBytes(file.url) : null);
        if (bytes) got.push({ path: file.path, bytes });
      } catch (err) {
        lastError = err;
      }
      onProgress?.(++done, wanted.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, wanted.length) }, worker));
  if (got.length === 0) throw lastError instanceof Error ? lastError : new Error("Couldn't fetch the audio.");

  if (wanted.length === 1) {
    const only = got[0]!;
    const filename = only.path.slice(only.path.lastIndexOf("/") + 1);
    saveBytes(filename, only.bytes, mimeOf(filename));
    return 1;
  }
  // The zip keeps the order asked for, whatever order the fetches finished in.
  const order = new Map(wanted.map((f, i) => [f.path, i]));
  got.sort((a, b) => order.get(a.path)! - order.get(b.path)!);
  const entries: Record<string, Uint8Array> = {};
  for (const f of got) entries[f.path] = f.bytes;
  saveBytes(`${safeFilename(name)}.zip`, zipSync(entries, { level: 0 }), "application/zip");
  return got.length;
}

/** Where a download was started. */
export type DownloadWhere = "seed_page" | "editor" | "materials" | "mine" | "clip_editor";

/** Count a whole-seed download: the `downloads` row and `seed_download`
 * when the seed exists, the event alone for a room that is not a seed yet. */
export function recordSeedDownload(seedId: string | undefined, where: DownloadWhere, files: number): void {
  if (seedId) {
    void webApi(`/seeds/${encodeURIComponent(seedId)}/download`, json("POST", { detail: where, surface: "web" })).catch(() => {});
    return;
  }
  track("seed_download", { detail: where, value: files });
}

/** Count a stem or clip download. */
export function recordDownload(event: Extract<AnalyticsEventName, "stem_download" | "clip_download">, detail: string, seedId?: string, value = 1): void {
  track(event, { detail, value, ...(seedId ? { seedId } : {}) });
}
